import { createAuthClient } from "better-auth/react";
import {
  COSMETIC_CATALOG,
  DEFAULT_COSMETIC_LOADOUT,
  equipCosmetic,
  normalizeCosmeticLoadout,
  RANKED_UNLOCK_XP,
  SPECIAL_ITEM_CATALOG,
  type CosmeticItem,
  type CosmeticLoadout,
  type SpecialItem
} from "@craftoff/game-core";
import { apiUrl, isNativeBuild, nativeApiOrigin } from "./platform";

const authClient = createAuthClient(isNativeBuild ? { baseURL: nativeApiOrigin } : undefined);

export type AccountSession = {
  playerId: string;
  username: string;
  xp: number;
  rankedRating: number;
  progressionVersion: number;
  guest: boolean;
  authenticated: boolean;
  needsUsername: boolean;
  email?: string;
  development?: boolean;
  cosmetics: CosmeticLoadout;
};

export type LeaderboardEntry = {
  username: string;
  xp: number;
  wins: number;
  losses: number;
};

export type AccountActivityDay = {
  date: string;
  matchCount: number;
  playedMs: number;
};

export type AccountStats = {
  wins: number;
  losses: number;
  totalMatches: number;
  totalPlayedMs: number;
  activeDays: number;
  trackingStartedAt: number;
  rangeStart: string;
  rangeEnd: string;
  timeZone: string;
  activity: AccountActivityDay[];
};

export type SpecialItemCounts = Record<string, number>;
export type ShopItem =
  | (CosmeticItem & { owned: boolean; equipped: boolean })
  | (SpecialItem & { owned: boolean; equipped: false; quantity: number });

export type WalletState = {
  coinBalance: number;
  unlimitedCoins?: boolean;
  highestXpAchieved: number;
  nextXpMilestone: number;
  xpRemaining: number;
  masterCraftsToday: number;
  masterCraftDailyLimit: number;
  rewardedAdsToday: number;
  rewardedAdDailyLimit: number;
  dailyResetAt: number;
};

export type ShopState = {
  status: "active";
  coinBalance: number;
  wallet: WalletState;
  rewardedAds: {
    enabled: boolean;
    rewardCoins: number;
    adUnitPath: string | null;
  };
  loadout: CosmeticLoadout;
  specialItemCounts: SpecialItemCounts;
  equippedSpecialItemId: string | null;
  items: ShopItem[];
};

export type ShopPurchaseResponse = {
  wallet: WalletState;
  ownedCosmeticIds?: string[];
  specialItemCounts?: SpecialItemCounts;
  equippedSpecialItemId?: string | null;
  itemId: string;
  purchased: boolean;
  coinsSpent: number;
};

export type MatchmakingResult =
  | { status: "waiting"; queueName: string; joinedAt: number }
  | { status: "not-queued"; queueName: string }
  | {
      status: "matched";
      queueName: string;
      matchId: string;
      opponentId: string;
      opponentUsername: string;
      opponentXp: number;
      opponentRating: number;
      opponentCosmetics: CosmeticLoadout;
      opponentSpecialItemId: string | null;
      matchedAt: number;
      token: string;
    };

const DEVELOPMENT_ACCOUNT: AccountSession = {
  playerId: "local_developer",
  username: "developer",
  xp: RANKED_UNLOCK_XP,
  rankedRating: RANKED_UNLOCK_XP,
  progressionVersion: 0,
  guest: false,
  authenticated: true,
  needsUsername: false,
  email: "developer@localhost",
  development: true,
  cosmetics: DEFAULT_COSMETIC_LOADOUT
};

const DEVELOPMENT_LEADERBOARD: LeaderboardEntry[] = [
  { username: "RedstonePro", xp: 1_740, wins: 31, losses: 8 },
  { username: "DiamondSmith", xp: 1_420, wins: 24, losses: 11 },
  { username: "developer", xp: RANKED_UNLOCK_XP, wins: 12, losses: 7 },
  { username: "NetherCrafter", xp: 860, wins: 9, losses: 10 },
  { username: "OakWorkbench", xp: 540, wins: 5, losses: 8 }
];

function localDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function developmentAccountStats(): AccountStats {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today);
  start.setDate(start.getDate() - 364);
  const activity: AccountActivityDay[] = [];
  for (let offset = 0; offset < 120; offset++) {
    if (offset % 3 === 1 || offset % 11 === 0) continue;
    const date = new Date(today);
    date.setDate(date.getDate() - offset);
    const matchCount = (offset % 4) + 1;
    const playedMs = matchCount * (6 + (offset % 8)) * 60_000;
    activity.push({ date: localDateKey(date), matchCount, playedMs });
  }
  return {
    wins: 12,
    losses: 7,
    totalMatches: 19,
    totalPlayedMs: 14 * 60 * 60_000 + 22 * 60_000,
    activeDays: activity.length,
    trackingStartedAt: new Date(today.getFullYear(), today.getMonth() - 4, today.getDate()).getTime(),
    rangeStart: localDateKey(start),
    rangeEnd: localDateKey(today),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    activity
  };
}

const DEVELOPMENT_COSMETICS_KEY = "craftoff-development-cosmetics-v1";
const DEVELOPMENT_ECONOMY_KEY = "craftoff-development-economy-v1";
const pendingPurchaseRequestIds = new Map<string, string>();
const pendingSpecialUseRequestIds = new Map<string, string>();
const developmentBattleUses = new Set<string>();
let developmentCosmetics = { ...DEFAULT_COSMETIC_LOADOUT };
let developmentEconomy: {
  coinBalance: number;
  ownedCosmeticIds: string[];
  rewardedAdsToday: number;
  specialItemCounts: SpecialItemCounts;
  equippedSpecialItemId: string | null;
} = {
  coinBalance: 250,
  ownedCosmeticIds: COSMETIC_CATALOG.filter((item) => item.coinPrice === 0).map((item) => item.id),
  rewardedAdsToday: 0,
  specialItemCounts: {},
  equippedSpecialItemId: null
};

function newRequestId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function loadDevelopmentCosmetics(): CosmeticLoadout {
  if (typeof window === "undefined") return developmentCosmetics;
  try {
    const saved = window.localStorage.getItem(DEVELOPMENT_COSMETICS_KEY);
    developmentCosmetics = normalizeCosmeticLoadout(saved ? JSON.parse(saved) as Partial<CosmeticLoadout> : developmentCosmetics);
  } catch {
    // Development selection still remains available for this browser session.
  }
  return developmentCosmetics;
}

function developmentShop(): ShopState {
  const loadout = loadDevelopmentCosmetics();
  if (typeof window !== "undefined") {
    try {
      const saved = window.localStorage.getItem(DEVELOPMENT_ECONOMY_KEY);
      if (saved) developmentEconomy = { ...developmentEconomy, ...JSON.parse(saved) as typeof developmentEconomy };
    } catch { /* Keep the in-memory development economy. */ }
  }
  const now = new Date();
  const reset = new Date(now);
  reset.setUTCHours(24, 0, 0, 0);
  const wallet: WalletState = {
    coinBalance: developmentEconomy.coinBalance,
    unlimitedCoins: true,
    highestXpAchieved: RANKED_UNLOCK_XP,
    nextXpMilestone: 5_000,
    xpRemaining: 4_000,
    masterCraftsToday: 0,
    masterCraftDailyLimit: 5,
    rewardedAdsToday: developmentEconomy.rewardedAdsToday,
    rewardedAdDailyLimit: 3,
    dailyResetAt: reset.getTime()
  };
  if (developmentEconomy.equippedSpecialItemId
    && (developmentEconomy.specialItemCounts[developmentEconomy.equippedSpecialItemId] ?? 0) <= 0) {
    developmentEconomy.equippedSpecialItemId = null;
  }
  return {
    status: "active",
    coinBalance: wallet.coinBalance,
    wallet,
    rewardedAds: { enabled: true, rewardCoins: 30, adUnitPath: null },
    loadout: { ...loadout },
    specialItemCounts: { ...developmentEconomy.specialItemCounts },
    equippedSpecialItemId: developmentEconomy.equippedSpecialItemId,
    items: [
      ...SPECIAL_ITEM_CATALOG.map((item) => ({
        ...item,
        quantity: developmentEconomy.specialItemCounts[item.id] ?? 0,
        owned: (developmentEconomy.specialItemCounts[item.id] ?? 0) > 0,
        equipped: false as const
      })),
      ...COSMETIC_CATALOG.map((item) => ({
        ...item,
        owned: developmentEconomy.ownedCosmeticIds.includes(item.id),
        equipped: item.id === loadout.wallpaperId || item.id === loadout.craftingTableId || item.id === loadout.faceplateId
      }))
    ]
  };
}

async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(apiUrl(path), {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init?.headers }
  });
  const body: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const code = typeof body === "object" && body && "error" in body && typeof body.error === "string"
      ? body.error
      : "REQUEST_FAILED";
    throw new Error(code);
  }
  return body as T;
}

export function loadAccountSession(): Promise<AccountSession> {
  if (import.meta.env.DEV) return Promise.resolve({ ...DEVELOPMENT_ACCOUNT, cosmetics: { ...loadDevelopmentCosmetics() } });
  return apiJson<AccountSession>("/api/session");
}

export function loadAccountStats(timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone): Promise<AccountStats> {
  if (import.meta.env.DEV) return Promise.resolve(developmentAccountStats());
  const query = timeZone ? `?timeZone=${encodeURIComponent(timeZone)}` : "";
  return apiJson<AccountStats>(`/api/account/stats${query}`);
}

export function loadCanonicalProgress(minVersion = 0): Promise<{ playerId: string; xp: number; rankedRating: number; version: number; current: boolean; coinBalance: number; coinReward: import("@craftoff/protocol").CoinRewardResult | null }> {
  return apiJson(`/api/progress?minVersion=${encodeURIComponent(String(minVersion))}`);
}

export function claimAccountProfile(username: string): Promise<AccountSession> {
  return apiJson("/api/account/claim", { method: "POST", body: JSON.stringify({ username }) });
}

export function leaveMatchmaking(queueName: string | null, ticketId: string, keepalive = false): Promise<void> {
  return fetch(apiUrl("/api/matchmaking/leave"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...(queueName ? { queueName } : {}), ticketId }),
    keepalive
  }).then((response) => {
    if (!response.ok && response.status !== 204) throw new Error("QUEUE_LEAVE_FAILED");
  });
}

export async function signInWithPassword(email: string, password: string): Promise<void> {
  const result = await authClient.signIn.email({ email, password });
  if (result.error) throw new Error(result.error.code ?? "SIGN_IN_FAILED");
}

export async function createAccountWithPassword(email: string, password: string): Promise<void> {
  const result = await authClient.signUp.email({
    email,
    password,
    name: "Craftoff Player"
  });
  if (result.error) throw new Error(result.error.code ?? "ACCOUNT_CREATION_FAILED");
}

export async function signOutAccount(): Promise<void> {
  if (import.meta.env.DEV) return;
  const result = await authClient.signOut();
  if (result.error) throw new Error(result.error.code ?? "SIGN_OUT_FAILED");
}

export function pendingAccountClaim(): { username: string } | null {
  const username = window.sessionStorage.getItem("craftoff_pending_username");
  return username ? { username } : null;
}

export function clearPendingAccountClaim(): void {
  window.sessionStorage.removeItem("craftoff_pending_username");
}

export async function loadLeaderboard(): Promise<LeaderboardEntry[]> {
  if (import.meta.env.DEV) return DEVELOPMENT_LEADERBOARD.map((entry) => ({ ...entry }));
  const response = await apiJson<{ entries: LeaderboardEntry[] }>("/api/leaderboard");
  return response.entries;
}

export async function loadShop(): Promise<ShopState> {
  if (import.meta.env.DEV) return developmentShop();
  const response = await apiJson<Omit<ShopState, "items"> & {
    items?: Array<CosmeticItem & { owned: boolean; equipped: boolean }>;
    ownedCosmeticIds?: string[];
  }>("/api/shop");
  const owned = new Set(response.ownedCosmeticIds ?? []);
  const loadout = normalizeCosmeticLoadout(response.loadout);
  const cosmeticItems = response.items ?? COSMETIC_CATALOG.map((item) => ({
    ...item,
    owned: owned.has(item.id),
    equipped: item.id === loadout.wallpaperId || item.id === loadout.craftingTableId || item.id === loadout.faceplateId
  }));
  return {
    ...response,
    loadout,
    specialItemCounts: { ...(response.specialItemCounts ?? {}) },
    items: [
      ...SPECIAL_ITEM_CATALOG.map((item) => ({
        ...item,
        quantity: response.specialItemCounts?.[item.id] ?? 0,
        owned: (response.specialItemCounts?.[item.id] ?? 0) > 0,
        equipped: false as const
      })),
      ...cosmeticItems
    ]
  };
}

export async function equipShopItem(itemId: string): Promise<CosmeticLoadout> {
  if (import.meta.env.DEV) {
    if (!developmentEconomy.ownedCosmeticIds.includes(itemId)) throw new Error("COSMETIC_NOT_OWNED");
    developmentCosmetics = equipCosmetic(loadDevelopmentCosmetics(), itemId);
    try { window.localStorage.setItem(DEVELOPMENT_COSMETICS_KEY, JSON.stringify(developmentCosmetics)); }
    catch { /* The in-memory development loadout remains active. */ }
    return { ...developmentCosmetics };
  }
  const response = await apiJson<{ loadout: CosmeticLoadout }>("/api/shop/equip", {
    method: "POST",
    body: JSON.stringify({ itemId })
  });
  return normalizeCosmeticLoadout(response.loadout);
}

export function applyShopPurchase(shop: ShopState, result: ShopPurchaseResponse): ShopState {
  const ownedCosmetics = result.ownedCosmeticIds ? new Set(result.ownedCosmeticIds) : null;
  const specialItemCounts = result.specialItemCounts
    ? { ...result.specialItemCounts }
    : shop.specialItemCounts;
  return {
    ...shop,
    coinBalance: result.wallet.coinBalance,
    wallet: result.wallet,
    specialItemCounts,
    equippedSpecialItemId: result.equippedSpecialItemId === undefined
      ? shop.equippedSpecialItemId
      : result.equippedSpecialItemId,
    items: shop.items.map((item) => item.category === "special"
      ? {
          ...item,
          quantity: specialItemCounts[item.id] ?? 0,
          owned: (specialItemCounts[item.id] ?? 0) > 0
        }
      : ownedCosmetics
        ? { ...item, owned: ownedCosmetics.has(item.id) }
        : item)
  };
}

export async function purchaseShopItem(itemId: string, currentShop: ShopState): Promise<ShopState> {
  if (import.meta.env.DEV) {
    const item = COSMETIC_CATALOG.find((candidate) => candidate.id === itemId);
    const special = SPECIAL_ITEM_CATALOG.find((candidate) => candidate.id === itemId);
    if (!item && !special) throw new Error("INVALID_SHOP_ITEM");
    if (special) {
      developmentEconomy.specialItemCounts[special.id] = (developmentEconomy.specialItemCounts[special.id] ?? 0) + 1;
    } else if (item && !developmentEconomy.ownedCosmeticIds.includes(itemId)) {
      developmentEconomy.ownedCosmeticIds.push(itemId);
    }
    try { window.localStorage.setItem(DEVELOPMENT_ECONOMY_KEY, JSON.stringify(developmentEconomy)); }
    catch { /* Keep the in-memory development economy. */ }
    return developmentShop();
  }
  const requestId = pendingPurchaseRequestIds.get(itemId) ?? newRequestId();
  pendingPurchaseRequestIds.set(itemId, requestId);
  try {
    const result = await apiJson<ShopPurchaseResponse>("/api/shop/purchase", {
      method: "POST",
      body: JSON.stringify({ itemId, requestId })
    });
    pendingPurchaseRequestIds.delete(itemId);
    return applyShopPurchase(currentShop, result);
  } catch (error) {
    const code = error instanceof Error ? error.message : "REQUEST_FAILED";
    if (code === "INVALID_PURCHASE" || code === "INSUFFICIENT_COINS" || code === "PURCHASE_REQUEST_REUSED") {
      pendingPurchaseRequestIds.delete(itemId);
    }
    throw error;
  }
}

export async function equipSpecialItem(itemId: string): Promise<string> {
  if (import.meta.env.DEV) {
    if ((developmentEconomy.specialItemCounts[itemId] ?? 0) <= 0) throw new Error("SPECIAL_ITEM_UNAVAILABLE");
    developmentEconomy.equippedSpecialItemId = itemId;
    try { window.localStorage.setItem(DEVELOPMENT_ECONOMY_KEY, JSON.stringify(developmentEconomy)); }
    catch { /* Keep the in-memory development loadout. */ }
    return itemId;
  }
  const response = await apiJson<{ equippedSpecialItemId: string }>("/api/special-items/equip", {
    method: "POST",
    body: JSON.stringify({ itemId })
  });
  return response.equippedSpecialItemId;
}

export type SpecialItemUseState = {
  specialItemCounts: SpecialItemCounts;
  equippedSpecialItemId: string | null;
};

export async function useSpecialItem(itemId: string, battleId: string, onlineMatch = false): Promise<SpecialItemUseState> {
  if (import.meta.env.DEV) {
    if (developmentEconomy.equippedSpecialItemId !== itemId) throw new Error("SPECIAL_ITEM_NOT_EQUIPPED");
    if (developmentBattleUses.has(battleId)) throw new Error("SPECIAL_ITEM_ALREADY_USED");
    const quantity = developmentEconomy.specialItemCounts[itemId] ?? 0;
    if (quantity <= 0) throw new Error("SPECIAL_ITEM_UNAVAILABLE");
    if (onlineMatch && (itemId === "freeze" || itemId === "scramble")) {
      const requestKey = `${battleId}:${itemId}`;
      const requestId = pendingSpecialUseRequestIds.get(requestKey) ?? newRequestId();
      pendingSpecialUseRequestIds.set(requestKey, requestId);
      try {
        await apiJson("/api/special-items/use", {
          method: "POST",
          body: JSON.stringify({ itemId, requestId, battleId })
        });
        pendingSpecialUseRequestIds.delete(requestKey);
      } catch (error) {
        const code = error instanceof Error ? error.message : "REQUEST_FAILED";
        if (code === "INVALID_SPECIAL_ITEM_USE" || code === "SPECIAL_ITEM_ALREADY_USED"
          || code === "SPECIAL_ITEM_ROUND_INACTIVE" || code === "SPECIAL_FREEZE_ACTIVE") {
          pendingSpecialUseRequestIds.delete(requestKey);
        }
        throw error;
      }
    }
    developmentBattleUses.add(battleId);
    developmentEconomy.specialItemCounts[itemId] = quantity - 1;
    if (developmentEconomy.specialItemCounts[itemId] <= 0) developmentEconomy.equippedSpecialItemId = null;
    try { window.localStorage.setItem(DEVELOPMENT_ECONOMY_KEY, JSON.stringify(developmentEconomy)); }
    catch { /* Keep the in-memory development economy. */ }
    return {
      specialItemCounts: { ...developmentEconomy.specialItemCounts },
      equippedSpecialItemId: developmentEconomy.equippedSpecialItemId
    };
  }
  const requestKey = `${battleId}:${itemId}`;
  const requestId = pendingSpecialUseRequestIds.get(requestKey) ?? newRequestId();
  pendingSpecialUseRequestIds.set(requestKey, requestId);
  try {
    const response = await apiJson<SpecialItemUseState>("/api/special-items/use", {
      method: "POST",
      body: JSON.stringify({ itemId, requestId, battleId })
    });
    pendingSpecialUseRequestIds.delete(requestKey);
    return {
      specialItemCounts: { ...response.specialItemCounts },
      equippedSpecialItemId: response.equippedSpecialItemId
    };
  } catch (error) {
    const code = error instanceof Error ? error.message : "REQUEST_FAILED";
    if (code === "INVALID_SPECIAL_ITEM_USE" || code === "SPECIAL_ITEM_UNAVAILABLE"
      || code === "SPECIAL_ITEM_NOT_EQUIPPED" || code === "SPECIAL_ITEM_ALREADY_USED") {
      pendingSpecialUseRequestIds.delete(requestKey);
    }
    throw error;
  }
}

export async function startRewardedAdSession(): Promise<{ sessionId: string; expiresAt: number; wallet: WalletState }> {
  if (import.meta.env.DEV) return { sessionId: newRequestId(), expiresAt: Date.now() + 600_000, wallet: developmentShop().wallet };
  return apiJson("/api/rewarded-ads/session", { method: "POST", body: "{}" });
}

export async function completeRewardedAdSession(sessionId: string): Promise<{ duplicate: boolean; coinsGranted: number; wallet: WalletState }> {
  if (import.meta.env.DEV) {
    if (developmentEconomy.rewardedAdsToday >= 3) throw new Error("REWARDED_AD_DAILY_LIMIT");
    developmentEconomy.rewardedAdsToday += 1;
    developmentEconomy.coinBalance += 30;
    try { window.localStorage.setItem(DEVELOPMENT_ECONOMY_KEY, JSON.stringify(developmentEconomy)); }
    catch { /* Keep the in-memory development economy. */ }
    return { duplicate: false, coinsGranted: 30, wallet: developmentShop().wallet };
  }
  return apiJson("/api/rewarded-ads/complete", {
    method: "POST",
    body: JSON.stringify({ sessionId })
  });
}
